// HTTP relay endpoint reference plus a live playground. Relay access needs
// an API token; the first section is the self-service way to get one (no
// sign-in, no identity check), and the rest default to using it.
import { el } from "./dom";
import { renderDropzone, formatBytes } from "./dropzone";
import { renderSnippetTabs } from "./codeSnippets";
import { showToast } from "./toast";
import { health, fetchPublicKey, registerPublicKey, uploadBlob, downloadBlob, requestApiToken, type ApiConfig } from "../lib/api";
import { apiTokenState, setApiToken } from "../lib/apiToken";
import { humanizeGeneric } from "../lib/humanizeError";
import { bytesToHex } from "../lib/bytes";
import type { TabId } from "../main";

function renderResultBox(): { element: HTMLElement; show: (ok: boolean, text: string) => void } {
  const box = el("pre", { class: "try-it-result" }, []);
  box.style.display = "none";
  return {
    element: box,
    show(ok: boolean, text: string) {
      box.style.display = "";
      box.className = `try-it-result ${ok ? "status-ok" : "status-error"}`;
      box.textContent = text;
    },
  };
}

function renderEndpointSection(
  title: string,
  method: string,
  path: string,
  description: string,
  snippetHttp: string,
  tryIt: HTMLElement,
): HTMLElement {
  return el("div", { class: "card" }, [
    el("p", { class: "section-label" }, [`${method} ${path}`]),
    el("h3", {}, [title]),
    el("p", { class: "section-sub" }, [description]),
    renderSnippetTabs({ http: snippetHttp }),
    tryIt,
  ]);
}

export function renderServiceApiPanel(apiConfig: ApiConfig, onNavigate: (tab: TabId) => void): HTMLElement {
  const baseUrl = apiConfig.baseUrl;
  const integrationLink = el("button", { type: "button", class: "link-btn" }, ["Integration guide"]);
  integrationLink.addEventListener("click", () => onNavigate("integration"));

  // --- POST /api-tokens ---
  const tokenResult = renderResultBox();
  const tokenStatus = el("p", { class: "section-sub", id: "svc-token-status" }, [
    apiTokenState.token ? "This session already has a token (see the sidebar)." : "No token yet.",
  ]);
  const tokenBtn = el("button", { type: "button", class: "secondary-btn" }, ["Get a token"]);
  tokenBtn.addEventListener("click", async () => {
    try {
      const res = await requestApiToken(baseUrl);
      setApiToken(res.api_key, res.expires_at); // this becomes the app's active token too, not just a demo value
      tokenResult.show(true, `201 Created\n${JSON.stringify(res, null, 2)}`);
      tokenStatus.textContent = "This session already has a token (see the sidebar).";
    } catch (e) {
      tokenResult.show(false, humanizeGeneric(e, "Request failed."));
    }
  });
  const tokenSection = el("div", { class: "card" }, [
    el("p", { class: "section-label" }, ["POST /api-tokens"]),
    el("h3", {}, ["Request an API token"]),
    el("p", { class: "section-sub" }, [
      "No sign-in, no identity check -- anyone can request one. Valid for about an hour, sent as X-Api-Key on every relay call after that. This is what the sections below (and this app's own upload/download actions) use by default once you have one.",
    ]),
    tokenStatus,
    renderSnippetTabs({ http: `curl -X POST ${baseUrl}/api-tokens` }),
    el("div", { class: "actions" }, [tokenBtn]),
    tokenResult.element,
  ]);

  // --- GET /health ---
  const healthResult = renderResultBox();
  const healthBtn = el("button", { type: "button", class: "secondary-btn" }, ["Try it"]);
  healthBtn.addEventListener("click", async () => {
    try {
      const res = await health(baseUrl);
      healthResult.show(true, JSON.stringify(res, null, 2));
    } catch (e) {
      healthResult.show(false, humanizeGeneric(e, "Request failed."));
    }
  });
  const healthSection = renderEndpointSection(
    "Liveness check",
    "GET",
    "/health",
    "No auth required. Useful for confirming the service is reachable before wiring anything else up.",
    `curl ${baseUrl}/health`,
    el("div", {}, [el("div", { class: "actions" }, [healthBtn]), healthResult.element]),
  );

  // --- GET /public-keys/{owner_id} ---
  const fetchOwnerInput = el("input", { type: "text", placeholder: "owner_id, e.g. alice-42", class: "mono-field" });
  const fetchResult = renderResultBox();
  const fetchBtn = el("button", { type: "button", class: "secondary-btn" }, ["Try it"]);
  fetchBtn.addEventListener("click", async () => {
    const owner = (fetchOwnerInput as HTMLInputElement).value.trim();
    if (!owner) {
      showToast("Enter an owner_id first.", "error");
      return;
    }
    try {
      const key = await fetchPublicKey(baseUrl, owner);
      fetchResult.show(true, `200 OK\n${bytesToHex(key)}`);
    } catch (e) {
      fetchResult.show(false, humanizeGeneric(e, "Request failed."));
    }
  });
  const fetchPublicKeySection = renderEndpointSection(
    "Fetch a public key",
    "GET",
    "/public-keys/{owner_id}",
    "No auth required - public keys are safe to share freely; having one only lets someone seal a message to that owner, never open one.",
    `curl ${baseUrl}/public-keys/<owner_id>`,
    el("div", {}, [fetchOwnerInput, el("div", { class: "actions" }, [fetchBtn]), fetchResult.element]),
  );

  // --- POST /public-keys/{owner_id} ---
  const registerOwnerInput = el("input", { type: "text", placeholder: "owner_id, e.g. alice-42", class: "mono-field" });
  const registerApiKeyInput = el("input", {
    type: "text",
    placeholder: "key_id.raw_key",
    class: "mono-field",
    autocomplete: "off",
    value: apiTokenState.token ?? "",
  });
  const registerDz = renderDropzone({
    id: "svc-register-pubkey-file",
    emptyTitle: "No public key file chosen",
    emptyMeta: "The raw 32-byte public key file from Generate keypair, or any file up to 1 KiB.",
  });
  const registerResult = renderResultBox();
  const registerBtn = el("button", { type: "button", class: "secondary-btn" }, ["Try it"]);
  registerBtn.addEventListener("click", async () => {
    const owner = (registerOwnerInput as HTMLInputElement).value.trim();
    const apiKey = (registerApiKeyInput as HTMLInputElement).value.trim();
    const file = registerDz.fileInput.files?.[0];
    if (!owner || !apiKey || !file) {
      showToast("Fill in owner_id, an API key, and choose a public key file.", "error");
      return;
    }
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const res = await registerPublicKey(baseUrl, owner, apiKey, bytes);
      registerResult.show(true, `201 Created\n${JSON.stringify(res, null, 2)}`);
    } catch (e) {
      registerResult.show(false, humanizeGeneric(e, "Request failed."));
    }
  });
  const registerPublicKeySection = renderEndpointSection(
    "Publish a public key",
    "POST",
    "/public-keys/{owner_id}",
    "Needs a static API key (X-Api-Key header) - either the self-service one requested above, or a permanent admin-issued key (see the Integration guide page for the difference).",
    `curl -X POST ${baseUrl}/public-keys/<owner_id> \\\n  -H "X-Api-Key: <key_id>.<raw_key>" \\\n  -H "Content-Type: application/octet-stream" \\\n  --data-binary @public.key`,
    el("div", {}, [
      registerOwnerInput,
      registerApiKeyInput,
      registerDz.element,
      el("div", { class: "actions" }, [registerBtn]),
      registerResult.element,
      el("p", { class: "section-sub" }, ["See the ", integrationLink, " page for self-service vs. permanent keys."]),
    ]),
  );

  // --- POST /blobs / GET /blobs/{id} ---
  const blobDz = renderDropzone({
    id: "svc-blob-upload-file",
    emptyTitle: "No file chosen",
    emptyMeta: "Any encrypted container, e.g. from Encrypt a file.",
    onFileChange: (f) => {
      if (f) blobDz.setDisplay(f.name, `${formatBytes(f.size)} · ready to upload`);
      else blobDz.reset();
    },
  });
  const blobUploadResult = renderResultBox();
  const blobUploadBtn = el("button", { type: "button", class: "secondary-btn" }, ["Try it"]);
  blobUploadBtn.addEventListener("click", async () => {
    const file = blobDz.fileInput.files?.[0];
    if (!file) {
      showToast("Choose a file first.", "error");
      return;
    }
    if (!apiTokenState.token) {
      showToast("Request an API token above first.", "error");
      return;
    }
    try {
      const res = await uploadBlob(apiConfig, file, apiTokenState.token);
      blobUploadResult.show(true, `200 OK\n${JSON.stringify(res, null, 2)}`);
    } catch (e) {
      blobUploadResult.show(false, humanizeGeneric(e, "Request failed."));
    }
  });
  const blobIdInput = el("input", { type: "text", placeholder: "blob_id", class: "mono-field" });
  const blobDownloadResult = renderResultBox();
  const blobDownloadBtn = el("button", { type: "button", class: "secondary-btn" }, ["Try it"]);
  blobDownloadBtn.addEventListener("click", async () => {
    const id = (blobIdInput as HTMLInputElement).value.trim();
    if (!id) {
      showToast("Enter a blob_id first.", "error");
      return;
    }
    if (!apiTokenState.token) {
      showToast("Request an API token above first.", "error");
      return;
    }
    try {
      const blob = await downloadBlob(apiConfig, id, apiTokenState.token);
      blobDownloadResult.show(true, `200 OK - ${formatBytes(blob.size)} of opaque ciphertext (not shown; this endpoint never returns anything readable)`);
    } catch (e) {
      blobDownloadResult.show(false, humanizeGeneric(e, "Request failed."));
    }
  });
  const blobsSection = renderEndpointSection(
    "Store and fetch an encrypted container",
    "POST",
    "/blobs, GET /blobs/{id}",
    "Needs an API token sent as X-Api-Key - either the self-service one requested above, or a permanent admin-issued key. The service validates that uploaded bytes parse as a container (see cryptocore/container.py) but never sees plaintext.",
    `curl -X POST ${baseUrl}/blobs \\\n  -H "X-Api-Key: <key_id>.<raw_key>" \\\n  --data-binary @sealed.enc\n\ncurl ${baseUrl}/blobs/<blob_id> \\\n  -H "X-Api-Key: <key_id>.<raw_key>" \\\n  -o sealed.enc`,
    el("div", {}, [
      blobDz.element,
      el("div", { class: "actions" }, [blobUploadBtn]),
      blobUploadResult.element,
      el("div", { class: "divider" }, []),
      blobIdInput,
      el("div", { class: "actions" }, [blobDownloadBtn]),
      blobDownloadResult.element,
    ]),
  );

  return el("section", {}, [
    el("p", { class: "eyebrow" }, ["Developer · Service API"]),
    el("h2", {}, ["HTTP endpoints for storage and public-key lookup"]),
    el("p", { class: "lede" }, [
      "The relay never sees plaintext, a password, or a private key - only ciphertext, public keys, and the API token needed to use it. Every request below actually runs against this service; nothing here is a mockup.",
    ]),
    tokenSection,
    healthSection,
    fetchPublicKeySection,
    registerPublicKeySection,
    blobsSection,
  ]);
}
